import {Handle} from "@luciad/ria/util/Evented.js";
import {BasicCreateController} from "@luciad/ria/view/controller/BasicCreateController.js";
import {EditController} from "@luciad/ria/view/controller/EditController.js";
import {Layer} from "@luciad/ria/view/Layer.js";
import {LayerTreeNode} from "@luciad/ria/view/LayerTreeNode.js";
import {LayerTreeVisitor} from "@luciad/ria/view/LayerTreeVisitor.js";
import {Map} from "@luciad/ria/view/Map.js";
import * as React from "react";
import {Bounds, SpatialModel} from "../../../model";
import {Bounds as RiaBounds} from "@luciad/ria/shape/Bounds.js";
import {Logger} from "../../util/Logger";
import {Spinner} from "../spinner/Spinner";
import {LayerTree} from "./LayerTree";
import {BackgroundMetadata} from "./model";
import {createDrawLayer, DRAW_ID} from "./RiaApi";
import * as riaApi from "./RiaApi";
import {Api} from "../../../api/InternalApi";
import {FormattedMessage} from "react-intl";
import {Button} from "react-bootstrap";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer.js";
import {setStateAsync} from "../../util/ReactUtils";
import {FeatureModel} from "@luciad/ria/model/feature/FeatureModel.js";
import {LineType} from "@luciad/ria/geodesy/LineType.js";
import {Feature, FeatureId} from "@luciad/ria/model/feature/Feature.js";
import {oidcProviderInfo} from "../../../oidc/startOidcProvider";
import {userManager} from "../../../oidc/utils/userManager";
import {oidcUtils} from "../../../oidc/utils/OidcUtils";
import {
  getFeatureFromWorkingSet,
  getSinglePolygonFeatureFromFeature,
  mergeFeatures
} from "../../../security/model";
import {SelectionType} from "@luciad/ria/view/SelectionType.js";
import {ShapeList} from "@luciad/ria/shape/ShapeList.js";
import {ShapeType} from "@luciad/ria/shape/ShapeType.js";
import {mapErrorToMessage} from "../../util/ErrorMapper";
import {Alert} from "react-bootstrap/lib";
import {ErrorDisplay} from "../errordisplay/ErrorDisplay";
import {getReference} from "@luciad/ria/reference/ReferenceProvider.js";
import * as TransformationFactory from "@luciad/ria/transformation/TransformationFactory.js";

interface RIAMapProps {
  wmsStyle?: string;
  style: React.CSSProperties;
  getLayernameFunction?: (data: SpatialModel) => string;
  getWmsUrlFunction?: (data: SpatialModel) => string;
  background?: BackgroundMetadata;
  wmsUrl?: string;
  api: Api;
  dataToVisualize: SpatialModel[];
  shouldFit: boolean;
  shouldAnimateFit: boolean;
  shouldAddGridLayer: boolean;
  shouldHideLayerTree?: boolean;
  wmsReference?: string;
  mapState?: object;
  onMapIdle?: (mapState: any) => void;
  restrictNavigationBounds?: Bounds;
  onMapInitialized?: (map: Map) => void;
  showPreview: boolean;
  backgroundDataPreview?: boolean;
  backgroundSequenceNumber?: number;
  drawModeActive?: boolean;
  editModeActive?: boolean;
  onDrawCancel?: () => void;
  onEditCancel?: () => void;
  onUseExistingArea?: () => void;
  onFileSpatialAreaCancel?: () => void;
  editFeature?: Feature;
  alwaysShowEditFeature?: boolean;
  onFeatureDrawn?: (feature: Feature) => void;
  button?: JSX.Element;
  polygonLayer?: FeatureLayer;
  polygonLayerReference?: string;
  enableComplexPolygon?: boolean;
  drawFillColor?: string;
}

interface RIAMapState {
  busyLoading: boolean;
  cancelDrawing: boolean;
  selectedFeatures: Feature[];
  editFeature: Feature;
  error: Error;
}

const waitForSize = (domNode: Element) => {
  return new Promise<void>((resolve) => {
    const checkSize = () => {
      const rect = domNode.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        resolve();
      } else {
        setTimeout(checkSize, 10);
      }
    };
    checkSize();
  });
};

export class RIAMap extends React.Component<RIAMapProps, RIAMapState> {
  _logger: Logger = Logger.getLogger("RIAMap");
  mapRef: React.ReactInstance;
  map: Map;
  initMapPromise: Promise<void>;
  mapIdleListener: Handle;
  backgroundLayers: Layer[];
  drawLayer: FeatureLayer;
  drawLayerWorkingSetChangedHandle: Handle;
  token: string = null;

  constructor(props: RIAMapProps) {
    super(props);
    this.state = {
      busyLoading: false,
      cancelDrawing: false,
      selectedFeatures: [],
      editFeature: null,
      error: null,
    };
  }

  createMap = (nextProps: RIAMapProps) => {
    //awkward... The component is mounted before the DOM node is styled and has a height
    this.initMapPromise = waitForSize(this.mapRef as Element).then(() => {
      this.initMap(nextProps);
    }).catch((_error) => {
      this._logger.error("Error occurred while initializing map");
    });
  }

  destroyMap = () => {
    if (this.mapIdleListener) {
      this.mapIdleListener.remove();
    }
    riaApi.destroyMap(this.map);
  }

  clearMap = () => {
    const nonBackgroundLayers = [];
    this.map.layerTree.visitChildren({
      visitLayer: (layer) => {
        if (this.backgroundLayers.indexOf(layer) < 0) {
          nonBackgroundLayers.push(layer);
        }
        return LayerTreeVisitor.ReturnValue.CONTINUE;
      },
      visitLayerGroup: () => {
        return LayerTreeVisitor.ReturnValue.CONTINUE;
      },
    }, LayerTreeNode.VisitOrder.TOP_DOWN);
    nonBackgroundLayers.forEach((nonBackgroundLayer) => this.map.layerTree.removeChild(nonBackgroundLayer));
  }

  initLayers = (props: RIAMapProps) => {
    const {
      wmsUrl, shouldFit, shouldAnimateFit, dataToVisualize,
      showPreview, polygonLayer, polygonLayerReference
    } = props;

    let aoiBounds = null;
    if (!showPreview) {
      if (this.props.editFeature?.shape) {
        aoiBounds = this.props.editFeature.shape;
      } else {
        aoiBounds = riaApi.getBounds(dataToVisualize);
      }
    }
    riaApi.setAreaOfInterest(this.map, aoiBounds);

    const canLoad = !!(wmsUrl && dataToVisualize && dataToVisualize.length && showPreview);
    this.setLoadingState(canLoad);

    if (canLoad) {
      const layerReference = this.map.reference;
      const defaultBounds = this.map.getMapBounds();

      const layersLoadedPromises = [];

      // RIA layers should be created in reversed order - last item is painted first
      const reversedData = [...dataToVisualize].reverse();

      reversedData.forEach((dataItem) => {
        const controlRoomBounds = dataItem.wgs84Bounds;
        const riaBounds = controlRoomBounds ? riaApi.createBounds(controlRoomBounds) : defaultBounds;
        const layerName = wmsUrl.substring(wmsUrl.lastIndexOf("/") + 1) + dataItem.id;
        const layerTitle = dataItem.title || dataItem.name;
        const url = wmsUrl + dataItem.id;

        const layer = this.props.wmsStyle ?
                      riaApi.addWMSLayerToMapWithStyle(this.map, url, layerName, layerTitle, riaBounds,
                          layerReference, this.props.wmsStyle) :
                      riaApi.addWMSLayerToMap(this.map, url, layerName, layerTitle, riaBounds, layerReference,
                          this.props.api.getPreviewBackgroundColor());
        layersLoadedPromises.push(layer.whenReady());
      });

      Promise.all(layersLoadedPromises).then(() => {
        setTimeout(() => this.setLoadingState(false), 500);
      }).catch((error) => {
        this._logger.error("Error occured while loading layers", error);
      });
    }
    if (polygonLayer) {
      this.map.layerTree.addChild(polygonLayer);
      if (shouldFit) {
        polygonLayer.whenReady().then(() => {
          const reactorBounds = dataToVisualize.map((dataItem) => {
            return dataItem.wgs84Bounds;
          });
          const featureCount = polygonLayer.workingSet.get();
          let intersects = false;
          const ref4326 = getReference("EPSG:4326");
          const refPolygonLayer = getReference(polygonLayerReference);
          const trans4326 = TransformationFactory.createTransformation(refPolygonLayer, ref4326);
          for (let i = 0; i < featureCount.length && !intersects; i++) {
            const featureBounds: RiaBounds = featureCount[i].shape.bounds;
            if (featureBounds && polygonLayerReference) {
              const wgs84FeatureBounds = trans4326.transformBounds(featureBounds);
              if (reactorBounds.length > 0) {
                reactorBounds.forEach((bounds: RiaBounds) => {
                  if (!intersects && wgs84FeatureBounds.interacts2D(bounds)) {
                    intersects = true;
                  }
                });
              } else {
                intersects = true;
              }
            }
          }
          if (intersects) {
            const wgs84Bounds = trans4326.transformBounds(polygonLayer.bounds);
            riaApi.fitOnBounds(this.map, [wgs84Bounds], shouldAnimateFit);
          } else {
            this.setState({
              error: new Error(
                  "There are no intersecting polygon features. Did you select the correct CRS for the GML/GeoJson file?")
            });
          }
        });
      }
      this.map.on("SelectionChanged", this.selectionChanged);
    }

    this.createDrawLayer();
    if (shouldFit) {
      const reactorBounds = dataToVisualize.map((dataItem) => {
        return dataItem.wgs84Bounds;
      });
      const editFeature = this.addDrawFeature();
      if (editFeature?.shape?.bounds) {
        riaApi.fitOnBounds(this.map, [editFeature.shape.bounds], shouldAnimateFit);
      } else if (reactorBounds.length > 0) {
        riaApi.fitOnBounds(this.map, reactorBounds, shouldAnimateFit);
      } else {
        const worldBounds = {x: -180, y: -88, width: 360, height: 88 * 2};
        riaApi.fitOnBounds(this.map, [worldBounds], shouldAnimateFit);
      }
    }
  }

  private createDrawLayer() {
    const layer = createDrawLayer(this.props.drawFillColor);
    this.map.layerTree.addChild(layer);
    this.drawLayer = layer;
  }

  private onDrawLayerWorkingSetChanged = (eventType: string, feature: any, _id: any) => {
    if (this.state.cancelDrawing) {
      this.clearDrawLayer(feature.id);
      return;
    }
    if (eventType === "add" || eventType === "update") {
      if (this.props.enableComplexPolygon && this.state.editFeature !== feature) {
        this.setState({editFeature: feature});
      }
      if (!this.props.enableComplexPolygon && this.state.editFeature !== feature) {
        if (feature.id > 1) {
          this.clearDrawLayer(feature.id - 1);
        }
        this.setState({editFeature: feature});
      }
    }
  };

  private clearDrawLayer(featureId: FeatureId) {
    this.drawLayerWorkingSetChangedHandle && this.drawLayerWorkingSetChangedHandle.remove();
    (this.drawLayer.model as FeatureModel).remove(featureId);
  }

  private onlySelectPolygons = (layer: any) => {
    const {enableComplexPolygon} = this.props;

    if (this.map.selectedObjects[0]?.selected.length > 0) {
      const removeObjects: Feature[] = [];
      (this.map.selectedObjects[0]?.selected as Feature[]).forEach(selectedObject => {
        const validShapes = [ShapeType.POLYGON, ShapeType.COMPLEX_POLYGON, ShapeType.SHAPE_LIST]
        if (!validShapes.find(el => el === selectedObject.shape.type)) {
          removeObjects.push(selectedObject);
        }
        if (selectedObject.shape.type === ShapeType.SHAPE_LIST) {
          const shapeList = selectedObject.shape as ShapeList;
          if (!enableComplexPolygon && shapeList.shapeCount > 1) {
            removeObjects.push(selectedObject);
          } else {
            for (let i = 0; i < shapeList.shapeCount; i++) {
              const shape = shapeList.getShape(i);
              if (shape.type !== ShapeType.POLYGON) {
                removeObjects.push(selectedObject);
              }
            }
          }
        }
        if (!enableComplexPolygon && selectedObject.shape.type === ShapeType.COMPLEX_POLYGON) {
          removeObjects.push(selectedObject);
        }
      });
      if (removeObjects.length > 0) {
        this.setState({error: new Error("Only Simple Polygons can be selected")});
      } else {
        this.setState({error: null});
      }
      this.map.selectObjects(
          [{
            layer,
            objects: removeObjects
          }], {
            editSelection: SelectionType.REMOVE
          }
      );
    }
  }

  private selectionChanged = (selectionChangeEvent: any) => {
    const {enableComplexPolygon} = this.props;
    const layer = selectionChangeEvent.selectionChanges[0].layer;

    if (enableComplexPolygon) {
      this.setState({selectedFeatures: this.map.selectedObjects[0].selected as Feature[]});
    } else {
      const selection = selectionChangeEvent.selectionChanges[0]?.selected[0];
      this.map.selectObjects(
          [{
            layer,
            objects: this.map.selectedObjects.length > 0 ? this.map.selectedObjects[0]?.selected.filter(
                selectedObject => selectedObject?.id !== selection?.id) as Feature[] : []
          }], {
            editSelection: SelectionType.REMOVE
          }
      );
      this.setState({
        selectedFeatures: this.map.selectedObjects[0]?.selected.length > 0
                          ? [this.map.selectedObjects[0].selected[0] as Feature] : []
      });
    }
    this.onlySelectPolygons(layer);
  }

  setLoadingState = (state: boolean) => {
    this.setState(Object.assign({}, this.state, {busyLoading: state}));
  }

  initMap = (nextProps: RIAMapProps) => {
    const props = nextProps ? nextProps : this.props;
    const {wmsReference, shouldAddGridLayer, restrictNavigationBounds, background} = props;

    if (props.backgroundDataPreview) {
      this.setLoadingState(true);
    }
    this.map = riaApi.createMap(this.mapRef,
        shouldAddGridLayer,
        restrictNavigationBounds,
        wmsReference,
        background,
        this.props.api.getPreviewBackgroundUrl(),
        this.props.api.getBackgroundDataName(),
        this.props.api.getPreviewBackgroundColor());
    this.backgroundLayers = [];
    const backgroundLayersLoadedPromises = [];
    //track background layers, these don't need to be removed upon switching layers
    this.map.layerTree.visitChildren({
      visitLayer: (layer) => {
        this.backgroundLayers.push(layer);
        if (props.backgroundDataPreview) {
          backgroundLayersLoadedPromises.push(layer.whenReady());
        }
        return LayerTreeVisitor.ReturnValue.CONTINUE;
      },
      visitLayerGroup: () => {
        return LayerTreeVisitor.ReturnValue.CONTINUE;
      },
    }, LayerTreeNode.VisitOrder.TOP_DOWN);

    if (props.backgroundDataPreview) {
      Promise.all(backgroundLayersLoadedPromises).then(() => {
        setTimeout(() => this.setLoadingState(false), 500);
      }).catch((error) => {
        this._logger.error("Error occurred while loading background layers", error);
      });
    } else {
      this.initLayers(props);
    }

    if (this.props.mapState) {
      this.map.restoreState(this.props.mapState).catch((error) => {
        this._logger.error("Error occurred while restoring map state", error);
      });
    }
    if (this.props.onMapIdle) {
      this.mapIdleListener = this.map.on("idle", () => {
        const state = this.map.saveState();
        this.props.onMapIdle(state);
      });
    }
    if (this.props.onMapInitialized) {
      this.props.onMapInitialized(this.map);
    }
  }

  checkToken = () => {
    const token = oidcUtils.getOidcTokenValue();
    const mapBoundsArray = this?.map?.getMapBounds();
    const mapBounds = mapBoundsArray.length > 0 ? mapBoundsArray[0] : null;
    const isMapValid = mapBounds &&
                       !(mapBounds.x === 0 && mapBounds.y === 0 && mapBounds.width === 0 && mapBounds.height === 0);

    if (isMapValid && this.token !== token) {
      this.token = token;
      userManager.getUser().then((oidcUser: Oidc.User) => {
        //The last 60 seconds before the token expires because of MaxSessionTimeout
        // AutomaticSilentRenew causes the token to keep refreshing, this stops the map flicker/reload
        if (oidcUser.expires_in > 60) {
          const bounds = mapBounds;
          riaApi.destroyMap(this.map);
          this.initMap(this.props);
          this.map.mapNavigator.fit({bounds, animate: false});
        }
      });
    }
  }

  componentDidMount() {
    if (oidcProviderInfo?.enabled) {
      this.token = oidcUtils.getOidcTokenValue();
      setInterval(this.checkToken, 1000);
    }
    this.createMap(null);
  }

  componentDidUpdate(prevProps: RIAMapProps) {
    if (!this.map) {
      return;
    }

    this.initMapPromise
        .then(() => {
          const {drawModeActive, editModeActive, alwaysShowEditFeature} = this.props;

          if (drawModeActive &&
              (!prevProps.drawModeActive || !(this.map.controller instanceof BasicCreateController))) {
            this.map.controller = new BasicCreateController(ShapeType.POLYGON, {});
            (this.drawLayer.model as FeatureModel).remove(DRAW_ID);
            this.drawLayerWorkingSetChangedHandle = this.drawLayer.workingSet.on("WorkingSetChanged",
                this.onDrawLayerWorkingSetChanged);
            this.state.cancelDrawing && this.updateState({cancelDrawing: false});
          } else if (editModeActive &&
                     (!prevProps.editModeActive || !(this.map.controller instanceof EditController))) {
            const editFeature = this.addDrawFeature();

            this.map.controller = this.createEditController(editFeature);

            this.drawLayerWorkingSetChangedHandle = this.drawLayer.workingSet.on("WorkingSetChanged",
                this.onDrawLayerWorkingSetChanged);
            this.state.cancelDrawing && this.updateState({cancelDrawing: false});
          } else if (!drawModeActive && !editModeActive) {
            this.map.controller = null;

            if (alwaysShowEditFeature && !(this.drawLayer.model as FeatureModel).store.get(DRAW_ID)) {
              this.addDrawFeature();
            }
          }
        });
  }

  private addDrawFeature() {
    const {editFeature} = this.props;
    if (!editFeature) {
      return null;
    }

    (this.drawLayer.model as FeatureModel).remove(DRAW_ID);
    (this.drawLayer.model as FeatureModel).add(editFeature);

    return editFeature;
  }

  private createEditController(editFeature: Feature) {
    const editController = new EditController(this.drawLayer, editFeature);

    // TODO: temporary solution, workaround to place edit handles on polygon edges
    // submitted to Luciad RIA support, Ticket#2021032910000141
    let editingContext = null;
    Object.defineProperty(editController, "_editingContext", {
      get: () => {
        return editingContext;
      },
      set: (v) => {
        editingContext = v;

        const geodesy = editingContext._handle._handles[0]._handles[0]._handles[2]._geodesy;
        const geodesyInterpolate = geodesy.interpolate.bind(geodesy);
        geodesy.interpolate = (startPoint: any, endPoint: any, fraction: any) => {
          return geodesyInterpolate(startPoint, endPoint, fraction, LineType.CONSTANT_BEARING);
        };

        editingContext._handle.update();
      }
    });

    return editController;
  }

  componentWillUnmount() {
    this.destroyMap();
  }

  wmsUlrHasChanged(nextProps: RIAMapProps) {
    return (this.props.wmsUrl && (this.props.wmsUrl !== nextProps.wmsUrl));
  }

  dataToVisualizeHasChanged(nextProps: RIAMapProps) {
    const thisData = this.props.dataToVisualize;
    const nextData = nextProps.dataToVisualize;
    if (thisData && nextData) {
      if (thisData.length !== nextData.length) {
        return true;
      }
      let someItemChanged = false;
      for (let i = 0; i < thisData.length; i++) {
        const idChanged = thisData[i].id !== nextData[i].id;
        const sequenceNumberChanged = thisData[i].sequenceNumber !== nextData[i].sequenceNumber;
        // Check if content has changed for styles
        const contentChanged = (thisData[i] as any).content && (nextData[i] as any).content &&
                               (thisData[i] as any).content !== (nextData[i] as any).content;
        someItemChanged = someItemChanged || (idChanged || sequenceNumberChanged || contentChanged);
        if (someItemChanged) {
          break;
        }
      }
      return someItemChanged;
    }
    //one of the two is null or undefined
    return nextData === thisData;
  }

  shouldPreviewHasChanged(nextProps: RIAMapProps) {
    return (this.props.showPreview !== nextProps.showPreview);
  }

  UNSAFE_componentWillReceiveProps(nextProps: RIAMapProps) {
    if (this.map && (this.wmsUlrHasChanged(nextProps) || this.dataToVisualizeHasChanged(nextProps) ||
                     this.shouldPreviewHasChanged(nextProps))) {
      this.clearMap();
      this.initLayers(nextProps);
    } else if (this.map && this.props.backgroundSequenceNumber && nextProps.backgroundSequenceNumber
               && this.props.backgroundSequenceNumber !== nextProps.backgroundSequenceNumber) {
      this.destroyMap();
      this.initMap(nextProps);
    }
  }

  updateState = (updatedPart: any) => this.setState(Object.assign({}, this.state, updatedPart));

  render() {
    const layerTree = this.props.shouldHideLayerTree ? null : <LayerTree map={this.map}/>;
    const loadingUI = this.state.busyLoading ? (<div className="spinnerContainer"><Spinner bigStyle={true}/></div>)
                                             : null;

    return (
        <div>
          {this.renderError()}
          <div className="riamap" data-cy="map" ref={(ref) => {
            this.mapRef = ref;
          }} style={this.props.style}>
            {this.renderButtons()}
            {loadingUI}
            {layerTree}
          </div>
        </div>
    );
  }

  private renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  private renderButtons() {
    if (!this.props.drawModeActive && !this.props.editModeActive && !this.props.polygonLayer) {
      return this.props.button;
    }
    const featureDrawn = (this.props.polygonLayer && this.state.selectedFeatures.length > 0) ||
                         (!this.props.polygonLayer && getFeatureFromWorkingSet(this.drawLayer?.workingSet.get()));
    return <div className="riamapButtons">
      {featureDrawn &&
       <Button data-cy="ok-drawing-btn" onClick={() => {
         const feature: Feature = (this.props.polygonLayer && this.state.selectedFeatures) ?
                                  mergeFeatures(this.state.selectedFeatures) :
                                  getFeatureFromWorkingSet(this.drawLayer.workingSet.get());
         if (this.props.enableComplexPolygon) {
           this.props.onFeatureDrawn(feature.copy());
         } else {
           this.props.onFeatureDrawn(getSinglePolygonFeatureFromFeature(feature).copy());
         }
       }}>
         <FormattedMessage
             id="studio.data-security-dialog.ok-button"
             defaultMessage="OK"/>
       </Button>
      }
      <Button data-cy="cancel-drawing-btn" onClick={this.handleCancelDrawing}>
        <FormattedMessage
            id="studio.ria-map.cancel-drawing"
            defaultMessage="Cancel"/>
      </Button>
    </div>
  }

  private handleCancelDrawing = () => {
    setStateAsync(this, {cancelDrawing: true})
        .then(() => {
          if (this.props.drawModeActive) {
            this.props.onDrawCancel();
          }
          if (this.props.editModeActive) {
            this.props.onEditCancel();
          }
          if (this.props.polygonLayer) {
            this.props.onFileSpatialAreaCancel();
          }
        });
  }
}